
import React, { useState, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { FeedbackForm } from '@/components/feedback-form';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MessageSquare, Users, User, ListCheck, Briefcase, UserPlus, Info, Clock } from 'lucide-react';
import { toast } from 'sonner';

type FeedbackMode = 'candidate' | 'recruiter' | 'client' | 'freelance' | 'manager' | 'peer';

const CreateFeedback = () => {
  const { profile } = useAuth();
  const [selectedMode, setSelectedMode] = useState<FeedbackMode>('candidate');
  const [availableModes, setAvailableModes] = useState<Array<{id: FeedbackMode, label: string, icon: React.ReactNode, description: string}>>([]);

  useEffect(() => {
    // Define available feedback modes based on user role
    const modes = [];
    
    // Add modes based on user role and potential profiles
    if (profile?.role === 'entreprise' || profile?.role === 'admin') {
      // Recruiters can give feedback to candidates
      modes.push({ 
        id: 'candidate', 
        label: 'Feedback pour un candidat', 
        icon: <User className="h-5 w-5 text-blue-500" />,
        description: 'Évaluez un candidat après un entretien'
      });
    }
    
    if (profile?.role === 'candidat' || profile?.role === 'admin') {
      // Candidates can give feedback to recruiters
      modes.push({ 
        id: 'recruiter', 
        label: 'Feedback pour un recruteur', 
        icon: <Users className="h-5 w-5 text-green-500" />,
        description: 'Partagez votre expérience avec un recruteur'
      });
    }
    
    // Everyone can give feedback to clients and freelancers
    modes.push({ 
      id: 'client', 
      label: 'Feedback pour un client', 
      icon: <Briefcase className="h-5 w-5 text-purple-500" />,
      description: 'Évaluez votre collaboration avec un client'
    });
    
    modes.push({ 
      id: 'freelance', 
      label: 'Feedback pour un freelance', 
      icon: <UserPlus className="h-5 w-5 text-amber-500" />,
      description: 'Évaluez la prestation d\'un freelance'
    });
    
    // Everyone can give feedback to managers and peers
    modes.push({ 
      id: 'manager', 
      label: 'Feedback pour un manager', 
      icon: <Users className="h-5 w-5 text-indigo-500" />,
      description: 'Évaluez les compétences managériales'
    });
    
    modes.push({ 
      id: 'peer', 
      label: 'Feedback pour un collègue', 
      icon: <User className="h-5 w-5 text-emerald-500" />,
      description: 'Partagez votre feedback sur un collègue'
    });
    
    setAvailableModes(modes);
    
    // Set default mode based on user role
    if (profile?.role === 'entreprise') {
      setSelectedMode('candidate');
    } else if (profile?.role === 'candidat') {
      setSelectedMode('recruiter');
    }
  }, [profile]);

  const getFeedbackTypeData = (mode: FeedbackMode) => {
    switch(mode) {
      case 'candidate':
        return {
          isRecruiter: true,
          contextLabel: 'recrutement',
          directionLabel: 'recruiter_to_candidate',
          title: 'Créer un feedback pour un candidat',
          description: 'Rédigez un feedback détaillé et constructif pour votre candidat'
        };
      case 'recruiter':
        return {
          isRecruiter: false,
          contextLabel: 'recrutement',
          directionLabel: 'candidate_to_recruiter',
          title: 'Créer un feedback pour un recruteur',
          description: 'Partagez votre expérience du processus de recrutement'
        };
      case 'client':
        return {
          isRecruiter: false,
          contextLabel: 'mission',
          directionLabel: 'freelance_to_client',
          title: 'Créer un feedback pour un client',
          description: 'Partagez votre expérience de collaboration avec ce client'
        };
      case 'freelance':
        return {
          isRecruiter: false,
          contextLabel: 'mission',
          directionLabel: 'client_to_freelance',
          title: 'Créer un feedback pour un freelance',
          description: 'Évaluez la prestation réalisée par ce freelance'
        };
      case 'manager':
        return {
          isRecruiter: false,
          contextLabel: 'managérial',
          directionLabel: 'employee_to_manager',
          title: 'Créer un feedback pour un manager',
          description: 'Partagez votre feedback sur les compétences managériales'
        };
      case 'peer':
        return {
          isRecruiter: false,
          contextLabel: 'collaboration',
          directionLabel: 'peer_to_peer',
          title: 'Créer un feedback pour un collègue',
          description: 'Partagez votre feedback sur votre collaboration'
        };
      default:
        return {
          isRecruiter: false,
          contextLabel: 'général',
          directionLabel: 'default',
          title: 'Créer un feedback',
          description: 'Partagez votre feedback'
        };
    }
  };

  const typeData = getFeedbackTypeData(selectedMode);

  const handleModeChange = (value: string) => {
    setSelectedMode(value as FeedbackMode);
    toast.info(`Mode de feedback changé: ${getFeedbackTypeData(value as FeedbackMode).title}`);
  };

  const renderModeSelector = () => {
    if (availableModes.length <= 1) return null;
    
    return (
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl">À qui souhaitez-vous donner un feedback ?</CardTitle>
          <CardDescription>
            Sélectionnez le destinataire de votre feedback
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col space-y-1">
              <Label htmlFor="feedback-recipient">Destinataire du feedback</Label>
              <Select 
                value={selectedMode} 
                onValueChange={handleModeChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionnez un destinataire" />
                </SelectTrigger>
                <SelectContent>
                  {availableModes.map((option) => (
                    <SelectItem 
                      key={option.id} 
                      value={option.id}
                      className="flex items-center gap-2"
                    >
                      <div className="flex items-center gap-2">
                        {option.icon}
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-2">
                {availableModes.find(m => m.id === selectedMode)?.description}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderContextInfo = () => {
    const mode = selectedMode;
    const selectedModeData = availableModes.find(m => m.id === mode);
    let infoItems: string[] = [];
    let bgColor = 'bg-blue-50';
    let borderColor = 'border-blue-100';
    let textColor = 'text-blue-800';
    let listColor = 'text-blue-700';
    
    // Set colors based on mode
    if (mode === 'candidate') {
      bgColor = 'bg-blue-50';
      borderColor = 'border-blue-100';
      textColor = 'text-blue-800';
      listColor = 'text-blue-700';
      infoItems = [
        'Après un processus de recrutement',
        'Suite à un entretien d\'embauche',
        'Pour donner un retour constructif',
        'Pour améliorer l\'expérience des candidats'
      ];
    } else if (mode === 'recruiter') {
      bgColor = 'bg-green-50';
      borderColor = 'border-green-100';
      textColor = 'text-green-800';
      listColor = 'text-green-700';
      infoItems = [
        'Après un processus de recrutement',
        'Suite à un ou plusieurs entretiens',
        'Pour partager votre expérience',
        'Pour aider à améliorer le processus'
      ];
    } else if (mode === 'freelance') {
      bgColor = 'bg-amber-50';
      borderColor = 'border-amber-100';
      textColor = 'text-amber-800';
      listColor = 'text-amber-700';
      infoItems = [
        'Après une collaboration',
        'À la fin d\'une mission',
        'Pour évaluer les livrables',
        'Pour améliorer la relation de travail'
      ];
    } else if (mode === 'client') {
      bgColor = 'bg-purple-50';
      borderColor = 'border-purple-100';
      textColor = 'text-purple-800';
      listColor = 'text-purple-700';
      infoItems = [
        'Après une prestation',
        'À la fin d\'un projet',
        'Pour partager votre expérience de collaboration',
        'Pour améliorer les services proposés'
      ];
    } else if (mode === 'manager' || mode === 'peer') {
      bgColor = 'bg-indigo-50';
      borderColor = 'border-indigo-100';
      textColor = 'text-indigo-800';
      listColor = 'text-indigo-700';
      
      if (mode === 'manager') {
        infoItems = [
          'Lors d\'une évaluation périodique',
          'Pour évaluer les compétences managériales',
          'Pour améliorer la relation manager-équipe',
          'Pour contribuer au développement professionnel'
        ];
      } else {
        infoItems = [
          'Après un projet d\'équipe',
          'Dans le cadre d\'une revue entre pairs',
          'Pour valoriser le travail d\'un collègue',
          'Pour améliorer la collaboration d\'équipe'
        ];
      }
    }
    
    return (
      <div className="container mx-auto px-4 pt-8">
        <div className="flex flex-col md:flex-row items-center mb-6 gap-3">
          <div className={`w-12 h-12 rounded-full ${bgColor} bg-opacity-50 flex items-center justify-center ${textColor}`}>
            {selectedModeData?.icon || <MessageSquare size={24} />}
          </div>
          <div>
            <h1 className="text-3xl font-bold">
              {typeData.title}
            </h1>
            <p className="text-gray-600 mt-1">
              {typeData.description}
            </p>
          </div>
        </div>
        
        <div className={`mb-8 ${bgColor} rounded-lg p-4 border ${borderColor}`}>
          <h2 className={`font-semibold ${textColor} mb-2 flex items-center`}>
            <Clock className="h-5 w-5 mr-2" />
            Quand donner un feedback ?
          </h2>
          <ul className={`list-disc list-inside ${listColor} space-y-1`}>
            {infoItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
        
        <Alert className="mb-8 bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-800">Bon à savoir</AlertTitle>
          <AlertDescription className="text-amber-700">
            Un feedback constructif est précis, factuel et équilibré. Prenez le temps de réfléchir à vos observations et soyez spécifique dans vos commentaires.
          </AlertDescription>
        </Alert>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center mb-6 gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-700">
              <MessageSquare size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">
                Créer un feedback
              </h1>
              <p className="text-gray-600 mt-1">
                Partagez votre retour d'expérience pour aider à améliorer les pratiques professionnelles
              </p>
            </div>
          </div>
          
          <Alert className="mb-8 bg-amber-50 border-amber-200">
            <Info className="h-4 w-4 text-amber-700" />
            <AlertTitle className="text-amber-800">Bon à savoir</AlertTitle>
            <AlertDescription className="text-amber-700">
              Un feedback constructif est précis, factuel et équilibré. Prenez le temps de réfléchir à vos observations et soyez spécifique dans vos commentaires.
            </AlertDescription>
          </Alert>
          
          {renderModeSelector()}
          
          {selectedMode && renderContextInfo()}
          
          <div className="max-w-3xl mx-auto">
            <FeedbackForm 
              isRecruiter={typeData.isRecruiter} 
              initialData={{
                contextType: typeData.contextLabel,
                directionType: typeData.directionLabel,
                title: typeData.title
              }}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CreateFeedback;
